import React, {
  useState,
  useEffect,
  useRef,
  useImperativeHandle,
  forwardRef,
} from "react";
import Chart from "chart.js/auto";

const timeLabels = ["1", "2", "3", "4", "5", "6", "7", "8"];

const drugParams = [
  "Induction Agents",
  "Muscle Relaxants",
  "Opioids",
  "Volatile Agents",
  "Local Anesthetics",
  "Reversal Agents",
  "Emergency Drugs",
  "Other Medications"
];

// Predefined common values for auto-suggestion
const drugSuggestions = {
  "Induction Agents": ["100", "150", "200"],
  "Muscle Relaxants": ["50", "75", "100"],
  "Opioids": ["25", "50", "75"],
  "Volatile Agents": ["1", "1.5", "2"],
  "Local Anesthetics": ["10", "20", "30"],
  "Reversal Agents": ["0.5", "1", "1.5"],
  "Emergency Drugs": ["0.5", "1", "2"],
  "Other Medications": ["5", "10", "15"]
};

const emptyGrid = () => drugParams.map(() => Array(timeLabels.length).fill(""));

const DrugAdministrationGrid = forwardRef((props, ref) => {
  const [gridData, setGridData] = useState(emptyGrid());
  const chartRef = useRef(null);
  const chartInstance = useRef(null);

  useImperativeHandle(ref, () => ({
    getGridData: () => gridData,
    setGridData: (data) => {
      if (Array.isArray(data) && data.length === drugParams.length) {
        const newData = data.map(row =>
          Array.isArray(row) ? row.slice() : Array(timeLabels.length).fill("")
        );
        setGridData(newData);
        setTimeout(updateChart, 100);
        props.onChange?.(newData);
      }
    }
  }));

  const restrictToNumbers = (e) => {
    const allowedKeys = [
      "Backspace", "Delete", "ArrowLeft", "ArrowRight", "Tab", "Enter", ".", "-"
    ];
    if (!/[0-9]/.test(e.key) && !allowedKeys.includes(e.key)) {
      e.preventDefault();
    }
  };

  function updateChart() {
    if (!chartInstance.current) return;
    chartInstance.current.data.datasets.forEach((dataset, i) => {
      if (!gridData[i]) return;
      dataset.data = gridData[i].map(val => {
        const num = parseFloat(val);
        return isNaN(num) ? null : num;
      });
    });
    chartInstance.current.update();
  }

  useEffect(() => {
    if (chartRef.current && !chartInstance.current) {
      chartInstance.current = new Chart(chartRef.current, {
        type: "line",
        data: {
          labels: timeLabels,
          datasets: drugParams.map((param, i) => ({
            label: param,
            data: [],
            borderColor: `hsl(${i * 45}, 70%, 50%)`,
            fill: false,
            tension: 0.3
          }))
        },
        options: {
          responsive: true,
          interaction: { mode: "index", intersect: false },
          scales: { y: { beginAtZero: true } },
          plugins: { legend: { position: "bottom" } }
        }
      });
    }
    updateChart();
    // eslint-disable-next-line
  }, [gridData]);

  return (
    <div className="bg-white p-4 rounded shadow-md">
      <table
        id="drugGrid"
        className="min-w-full text-sm text-center border border-gray-300"
      >
        <thead className="bg-gray-100">
          <tr>
            <th className="border px-2 py-1">Drugs ↓ / Time →</th>
            {timeLabels.map(time => (
              <th key={time} className="border px-2 py-1">{time}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {drugParams.map((param, rowIdx) => (
            <tr key={param} data-parameter={param}>
              <td className="border font-medium bg-gray-50 px-2 py-1 text-left">
                {param}
              </td>
              {timeLabels.map((_, colIdx) => (
                <td
                  key={colIdx}
                  className="border px-1 py-1 hover:bg-blue-50 focus-within:bg-blue-100 transition-colors relative"
                >
                  <input
                    className="w-full text-sm px-1 py-0.5 bg-transparent outline-none focus:ring-1 focus:ring-blue-300"
                    type="text"
                    inputMode="decimal"
                    pattern="[0-9]*"
                    value={gridData[rowIdx][colIdx]}
                    list={`${param.replace(/\s+/g, '')}-list`}
                    onChange={e => {
                      const newGrid = gridData.map(row => row.slice());
                      newGrid[rowIdx][colIdx] = e.target.value;
                      setGridData(newGrid);
                      props.onChange?.(newGrid);
                    }}
                    onKeyPress={restrictToNumbers}
                    placeholder="–"
                  />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>

      {/* Auto-fill datalists for suggestions */}
      {Object.entries(drugSuggestions).map(([param, values]) => (
        <datalist id={`${param.replace(/\s+/g, '')}-list`} key={param}>
          {values.map(val => (
            <option key={val} value={val} />
          ))}
        </datalist>
      ))}

      {/* <div className="mt-6 text-sm text-gray-600 font-medium">
        Drug Dosage Chart (Numerical only)
      </div>
      <canvas ref={chartRef} height="140" className="mt-2" /> */}
    </div>
  );
});

export default DrugAdministrationGrid;
